import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { field } from "@/components/ui-kit";

const GROUPS = [
  ["Partner", [["go_name", "Warehouse / party name (940 N1*WH)"], ["go_wh_code", "WH code"], ["go_address", "Address"], ["go_city", "City"], ["go_state", "State"], ["go_zip", "Zip"], ["contact_name", "EDI contact name"], ["contact_email", "EDI contact email"]]],
  ["EDI interchange IDs", [["go_isa_qualifier", "ISA qualifier (ISA07)"], ["go_isa_id", "ISA ID - production"], ["go_gs_id", "GS ID - production"], ["go_isa_id_test", "ISA ID - test"], ["go_gs_id_test", "GS ID - test"]]],
  ["EDIFACT (UNB) - only for EDIFACT partners e.g. Casino", [["ep_unb_id", "EP GLN - UNB sender / NAD SU"], ["ep_unb_qual", "EP qualifier (14 = GLN)"], ["go_unb_id", "Partner UNB ID - production"], ["go_unb_id_test", "Partner UNB ID - test"], ["go_unb_qual", "Partner qualifier (14 = GLN)"], ["ep_vat_no", "EP VAT number (INVOIC)"], ["vat_rate", "VAT rate % on INVOIC"], ["edi_currency", "Invoice currency"], ["edi_version", "EDIFACT version (blank = D96A, D01B = EANCOM 2002)"], ["unb_app_ref", "UNB application ref (e.g. BELU_V3)"], ["unb_app_ref_invoic", "UNB application ref for INVOIC (e.g. INVDMT_V3)"], ["go_vat_no", "Partner VAT number (INVOIC NAD BY / IV)"], ["vat_category", "VAT category when 0% (e.g. AE reverse charge)"]]],
  ["AS2", [["go_as2_id", "Partner AS2 ID"], ["go_as2_url", "Partner AS2 URL"], ["go_sending_ip", "Partner sending IP"], ["as2_enc_alg", "Encryption (blank = EP default)"], ["as2_digest", "Signing (blank = EP default)"], ["as2_mdn_mode", "MDN SYNC / ASYNC (blank = EP default)"]]],
];

const blank = (types, defaults) => ({ code: "", name: "", kind: "3pl", active: true, messages: { ...((defaults || {})["3pl"] || Object.fromEntries(Object.keys(types).map((k) => [k, true]))) }, cfg: { go_isa_qualifier: "ZZ" } });

export const PartnerDialog = ({ open, onOpenChange, partner, types, defaults, onSaved }) => {
  const [f, setF] = useState(blank(types, defaults));
  useEffect(() => {
    if (open) setF(partner ? { ...partner, cfg: partner.cfg || {} } : blank(types, defaults));
  }, [open, partner, types, defaults]);
  const setCfg = (k, v) => setF({ ...f, cfg: { ...f.cfg, [k]: v } });
  const save = async () => {
    try {
      partner ? await api.put(`/partners/${partner.code}`, f) : await api.post("/partners", f);
      toast.success(`${f.name} saved`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-slate-800 bg-[#1F232C] text-slate-100" data-testid="partner-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{partner ? `Edit ${partner.name}` : "Add trading partner"}</DialogTitle>
          <DialogDescription className="text-slate-400">Your EP AS2 ID, EP ISA / GS IDs and EP certificate are shared by all partners (Partner Settings).{partner?.idoc?.enabled ? ` Adding EDI details keeps this customer's SAP IDoc settings and ${(partner.idoc.delivery_points || []).length} depot(s) - EDI orders use them straight away.` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs text-slate-400">Name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`mt-1 ${field}`} data-testid="partner-name" /></label>
          <label className="text-xs text-slate-400">Code<Input value={f.code} disabled={!!partner} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="e.g. ACME3PL" className={`mt-1 font-mono ${field}`} data-testid="partner-code" /></label>
          <label className="text-xs text-slate-400">Type
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value, ...(partner ? {} : { messages: { ...((defaults || {})[e.target.value] || f.messages) } }) })} className={`mt-1 h-9 w-full rounded-md border px-2 text-sm ${field}`} data-testid="partner-kind">
              <option value="3pl">3PL / warehouse</option>
              <option value="customer">Direct EDI customer</option>
            </select>
          </label>
        </div>
        {GROUPS.map(([title, fields]) => (
          <div key={title}>
            <div className="mb-2 mt-1 text-[11px] uppercase tracking-wider text-slate-500">{title}</div>
            <div className="grid gap-3 sm:grid-cols-3">
              {fields.map(([k, label]) => (
                <label key={k} className="text-xs text-slate-400">{label}<Input value={f.cfg[k] || ""} onChange={(e) => setCfg(k, e.target.value)} className={`mt-1 font-mono text-xs ${field}`} data-testid={`partner-cfg-${k}`} /></label>
              ))}
            </div>
          </div>
        ))}
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Message types</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {Object.entries(types).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-xs text-slate-300">
              <Switch checked={!!f.messages[k]} onCheckedChange={(v) => setF({ ...f, messages: { ...f.messages, [k]: v } })} data-testid={`partner-msg-${k}`} /> {label}
            </label>
          ))}
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="partner-active" /> Active
          </label>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={!f.name.trim()} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="partner-save-btn">Save partner</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
